/**
 * Order Lookup Web Component
 * Warranty claim flow: form → claim (edit → review → submit).
 * Backend contract: POST {orderName, email} → {success, order: {id, name, orderNumber, fulfillments, customer, warrantyDates}}
 *   fulfillments = [{ id, status, documentId, docNumber?, items: [{ lineItemId, sku?, title, imageUrl?, eligibleQuantity }] }]
 *   customer = { isB2B, addresses?: [{id, formattedAddress}], locations?: [{id, name, formattedAddress}] }
 *   warrantyDates = { [documentId]: { [sku]: 'YYYY-MM-DD' } }
 */
if (!customElements.get('order-lookup')) {
  class OrderLookup extends HTMLElement {
    constructor() {
      super();
      this.currentOrder = null;

      // Config from data attributes
      this.apiEndpoint = this.dataset.apiEndpoint;
      this.returnEndpoint = this.dataset.returnEndpoint || '';

      // Load translations (theme provides these via Liquid)
      const translationsEl = this.querySelector('script[data-translations]');
      this.t = translationsEl ? JSON.parse(translationsEl.textContent) : {};

      // Cache DOM elements
      this.views = {
        form: this.querySelector('[data-view="form"]'),
        claim: this.querySelector('[data-view="claim"]')
      };

      this.form = this.querySelector('[data-order-form]');
      this.submitBtn = this.querySelector('[data-submit-btn]');
      this.errorContainer = this.querySelector('[data-error-container]');
      this.errorMessage = this.querySelector('[data-error-message]');

      // Templates (lazy)
      this._templates = null;

      this.claimableItems = [];
      // itemKey → array of { quantity, reason, solution, solutionLabel }.
      // Empty array means the item has no claim groups yet (initial render adds one zero-qty group).
      this.itemClaims = new Map();
      this.lookupEmail = '';
      this.selectedAddress = null; // { id, type: 'location'|'address', display }
      this.fulfillmentGroups = []; // [{ documentId, items: [claimableItem] }]
      this.orderNumber = '';

      this.bindEvents();
    }

    get templates() {
      if (!this._templates) {
        this._templates = {
          returnForm: this.querySelector('[data-template="return-form"]'),
          returnItem: this.querySelector('[data-template="return-item"]'),
          claimGroup: this.querySelector('[data-template="claim-group"]'),
          returnReview: this.querySelector('[data-template="return-review"]'),
          reviewItem: this.querySelector('[data-template="review-item"]')
        };
      }
      return this._templates;
    }

    // === Claim group helpers ===

    getClaimGroups(itemKey) {
      return this.itemClaims.get(itemKey) || [];
    }

    setClaimGroups(itemKey, groups) {
      if (groups.length === 0) this.itemClaims.delete(itemKey);
      else this.itemClaims.set(itemKey, groups);
    }

    parseGroupTarget(targetEl) {
      const itemEl = targetEl?.closest('[data-return-item]');
      const groupEl = targetEl?.closest('[data-claim-group]');
      const itemKey = itemEl?.dataset?.lineItemId;
      const groupIndex = groupEl ? Number(groupEl.dataset.groupIndex) : -1;
      if (!itemKey || !Number.isInteger(groupIndex) || groupIndex < 0) return null;
      return { itemKey, groupIndex, itemEl, groupEl };
    }

    bindEvents() {
      this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
      this.addEventListener('change', (e) => {
        const returnItemQtySelect = e.target.closest('[data-return-qty-select]');
        if (returnItemQtySelect) {
          const target = this.parseGroupTarget(returnItemQtySelect);
          if (target) this.handleGroupQty(target, returnItemQtySelect.value);
          return;
        }
        const addressSelect = e.target.closest('[data-address-select]');
        if (addressSelect) {
          this.handleAddressSelect(addressSelect.value);
          return;
        }
        const solutionSelect = e.target.closest('[data-item-solution]');
        if (solutionSelect) {
          const target = this.parseGroupTarget(solutionSelect);
          if (target) {
            const selectedOption = solutionSelect.options[solutionSelect.selectedIndex];
            this.handleGroupSolution(target, solutionSelect.value, selectedOption?.text || '');
          }
        }
      });
      this.addEventListener('input', (e) => {
        const reasonInput = e.target.closest('[data-item-reason]');
        if (reasonInput) {
          const target = this.parseGroupTarget(reasonInput);
          if (target) this.handleGroupReason(target, reasonInput.value);
          return;
        }
        if (e.target.tagName === 'TEXTAREA') {
          this.autoResizeTextarea(e.target);
        }
      });
      this.addEventListener('click', (e) => {
        const addGroupBtn = e.target.closest('[data-add-group]');
        if (addGroupBtn) {
          const itemEl = addGroupBtn.closest('[data-return-item]');
          const itemKey = itemEl?.dataset?.lineItemId;
          if (itemKey) this.handleAddGroup(itemKey);
          return;
        }
        const removeGroupBtn = e.target.closest('[data-remove-group]');
        if (removeGroupBtn) {
          const target = this.parseGroupTarget(removeGroupBtn);
          if (target) this.handleRemoveGroup(target);
          return;
        }
        if (e.target.closest('[data-cancel-return]')) {
          this.resetClaimState();
          this.fulfillmentGroups = [];
          this.orderNumber = '';
          this.claimableItems = [];
          this.currentOrder = null;
          this.lookupEmail = '';
          this.form?.reset();
          this.hideError();
          this.showView('form');
          return;
        }
        if (e.target.closest('[data-continue-return]')) {
          this.showReviewStep();
          return;
        }
        if (e.target.closest('[data-back-to-edit]')) {
          this.renderClaimForm();
          return;
        }
        if (e.target.matches('[data-submit-return]')) this.submitReturnRequest();
      });
    }

    resetClaimState() {
      this.itemClaims.clear();
      this.selectedAddress = null;
    }

    async handleSubmit(e) {
      e.preventDefault();
      const orderName = this.form.orderName?.value?.trim();
      const email = this.form.email.value.trim();

      await this.doLookup(orderName, email);
    }

    async doLookup(orderName, email) {
      if (!orderName || !email) {
        return this.showError(this.t.errorRequired);
      }
      if (!this.apiEndpoint) {
        return this.showError(this.t.errorConfig);
      }
      if (!this.returnEndpoint) {
        return this.showError(this.t.errorConfig);
      }

      this.setLoading(true);
      this.hideError();

      try {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderName, email })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          const errors = {
            order_not_found: this.t.errorNotFound,
            invalid_email: this.t.errorInvalidEmail,
            invalid_phone: this.t.errorInvalidPhone,
            invalid_order_name: this.t.errorInvalidOrderName,
            rate_limited: this.t.errorRateLimit
          };
          const errorKey = String(data.error || '').toLowerCase().replace(/[\s-]+/g, '_');
          return this.showError(errors[errorKey] || this.t.errorGeneric);
        }

        const order = data.order;
        if (!order) {
          return this.showError(this.t.errorNotFound);
        }

        this.currentOrder = { ...order, customer: data.customer || order.customer };
        const { orderNumber, fulfillmentGroups, allItems } = this.calculateClaimableItems(order);
        this.orderNumber = orderNumber;
        this.fulfillmentGroups = fulfillmentGroups;
        this.claimableItems = allItems;
        this.resetClaimState();
        // Snapshot the email used for this lookup so submit sends the same value
        // the backend validated against.
        this.lookupEmail = email;

        if (this.claimableItems.length === 0) {
          this.renderNoEligibleItems();
          this.showView('claim');
          return;
        }

        this.renderClaimForm();
        this.showView('claim');
      } catch {
        this.showError(this.t.errorNetwork);
      } finally {
        this.setLoading(false);
      }
    }

    // Pure computation — no side effects. Callers own the state assignment.
    calculateClaimableItems(order) {
      const orderNumber = String(order?.orderNumber || order?.name || '');
      const fulfillments = Array.isArray(order?.fulfillments) ? order.fulfillments : [];
      const warrantyDates = order?.warrantyDates && typeof order.warrantyDates === 'object' ? order.warrantyDates : {};
      const allItems = [];
      const fulfillmentGroups = [];

      for (const f of fulfillments) {
        const fItems = Array.isArray(f.items) ? f.items : [];
        const documentId = f.documentId || '';
        const skuWarranty = warrantyDates[documentId] || {};
        // Always namespace itemKey by fulfillment so the same line item shipped across
        // multiple fulfillments produces distinct claim entries.
        const fulfillmentScope = documentId || f.id || '';
        const mappedItems = fItems
          .filter(item => item.lineItemId && Number(item.eligibleQuantity) > 0)
          .map(item => {
            return {
              itemKey: fulfillmentScope ? `${fulfillmentScope}:${item.lineItemId}` : item.lineItemId,
              lineItemId: item.lineItemId,
              sku: item.sku || '',
              title: item.title || item.sku || '',
              imageUrl: item.imageUrl || '',
              eligibleQuantity: Number(item.eligibleQuantity),
              documentId,
              orderNumber,
              warrantyUntil: item.sku ? (skuWarranty[item.sku] ?? null) : null
            };
          });

        if (mappedItems.length > 0) {
          fulfillmentGroups.push({ documentId, docNumber: f.docNumber || null, items: mappedItems });
          allItems.push(...mappedItems);
        }
      }

      // Fallback: support old flat items format
      if (allItems.length === 0 && Array.isArray(order?.items) && order.items.length > 0) {
        const fallbackItems = order.items
          .filter(item => item.lineItemId && Number(item.eligibleQuantity) > 0)
          .map(item => ({
            itemKey: item.lineItemId,
            lineItemId: item.lineItemId,
            sku: item.sku || '',
            title: item.title || item.sku || '',
            imageUrl: item.imageUrl || '',
            eligibleQuantity: Number(item.eligibleQuantity),
            documentId: '',
            orderNumber,
            warrantyUntil: null
          }));
        if (fallbackItems.length > 0) {
          fulfillmentGroups.push({ documentId: '', docNumber: null, items: fallbackItems });
          allItems.push(...fallbackItems);
        }
      }

      return { orderNumber, fulfillmentGroups, allItems };
    }

    getRequestReturnEndpoint() {
      if (this.returnEndpoint && this.returnEndpoint.trim()) return this.returnEndpoint.trim();
      if (!this.apiEndpoint) return '';
      try {
        const url = new URL(this.apiEndpoint);
        const path = url.pathname.replace(/\/[^/]*$/, '') + '/requestReturn';
        url.pathname = path;
        return url.toString();
      } catch {
        return this.apiEndpoint.replace(/\/[^/]*$/, '') + '/requestReturn';
      }
    }

    // === Rendering ===

    get claimContainer() {
      return this.views.claim;
    }

    renderClaimForm() {
      const container = this.claimContainer;
      if (!this.templates.returnForm || !this.templates.returnItem || !container) return;

      container.replaceChildren();
      const formEl = this.templates.returnForm.content.cloneNode(true);
      const itemsContainer = formEl.querySelector('[data-return-items]');

      // Append order name to the title already set by the Liquid template
      const titleEl = formEl.querySelector('[data-return-form-title]');
      const orderName = this.currentOrder?.name || '';
      if (orderName && titleEl) {
        const orderLabel = this.dataset.labelOrder || 'Order';
        titleEl.textContent = `${titleEl.textContent} \u2014 ${orderLabel}: ${orderName}`;
      }

      // Render items grouped by fulfillment
      let itemIndex = 0;
      this.fulfillmentGroups.forEach(group => {
        const headingText = this.formatFulfillmentHeading(group);
        if (headingText) {
          const heading = document.createElement('div');
          heading.className = 'return-form__fulfillment-heading';
          heading.textContent = headingText;
          itemsContainer.appendChild(heading);
        }

        group.items.forEach(item => {
          const row = this.templates.returnItem.content.cloneNode(true);
          const itemEl = row.querySelector('[data-return-item]');
          itemEl.dataset.lineItemId = item.itemKey;

          row.querySelector('[data-title]').textContent = item.title;
          const skuEl = row.querySelector('[data-sku]');
          if (skuEl) {
            if (item.sku) skuEl.textContent = `SKU: ${item.sku}`;
            else skuEl.remove();
          }

          this.appendWarrantyBadge(row.querySelector('.return-item__info'), item);

          this.renderItemImage(row.querySelector('[data-image]'), item.imageUrl);

          if (!this.itemClaims.has(item.itemKey)) {
            this.itemClaims.set(item.itemKey, [this.makeEmptyClaimGroup()]);
          }
          const groupsContainer = row.querySelector('[data-claim-groups]');
          const claimGroups = this.getClaimGroups(item.itemKey);
          claimGroups.forEach((claim, gIdx) => {
            const groupEl = this.buildClaimGroupElement(item, claim, gIdx, claimGroups.length, itemIndex);
            groupsContainer.appendChild(groupEl);
            itemIndex++;
          });

          itemsContainer.appendChild(row);
        });
      });

      container.appendChild(formEl);
      this.initTextareasInEl(container);
      // Apply remaining-capacity constraints across sibling claim groups.
      for (const itemKey of this.itemClaims.keys()) {
        this.refreshQtyOptionsForItem(itemKey);
      }
      this.updateContinueButton();
    }

    autoResizeTextarea(el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }

    initTextareasInEl(el) {
      el.querySelectorAll('textarea').forEach(ta => this.autoResizeTextarea(ta));
    }

    formatAddressDisplay(opt, isB2B) {
      if (isB2B) {
        const parts = [opt.name, opt.formattedAddress].filter(Boolean);
        return parts.join(' \u2014 ');
      }
      return opt.formattedAddress || opt.id || '';
    }

    getCustomerAddressOptions() {
      const customer = this.currentOrder?.customer;
      const isB2B = customer?.isB2B === true;
      const options = isB2B
        ? (Array.isArray(customer?.locations) ? customer.locations : [])
        : (Array.isArray(customer?.addresses) ? customer.addresses : []);
      return { isB2B, options };
    }

    renderAddressSelector(container) {
      const { isB2B, options } = this.getCustomerAddressOptions();
      if (options.length === 0) return;

      const labelText = isB2B
        ? (this.t.selectLocation || 'Select location')
        : (this.t.selectAddress || 'Select address');

      const label = document.createElement('label');
      label.setAttribute('for', 'return-address-select');
      label.textContent = labelText;

      const select = document.createElement('select');
      select.id = 'return-address-select';
      select.dataset.addressSelect = '';

      const emptyOpt = document.createElement('option');
      emptyOpt.value = '';
      emptyOpt.textContent = labelText;
      select.appendChild(emptyOpt);

      options.forEach(opt => {
        const optEl = document.createElement('option');
        optEl.value = opt.id || '';
        optEl.textContent = this.formatAddressDisplay(opt, isB2B);
        select.appendChild(optEl);
      });

      if (this.selectedAddress) {
        select.value = this.selectedAddress.id;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'return-form__address-field is-required';
      wrapper.appendChild(label);
      wrapper.appendChild(select);
      container.appendChild(wrapper);
    }

    renderNoEligibleItems() {
      const container = this.claimContainer;
      if (!container) return;

      container.replaceChildren();

      const wrapper = document.createElement('div');
      wrapper.className = 'return-form';

      const orderName = this.currentOrder?.name || '';
      if (orderName) {
        const orderLabel = this.dataset.labelOrder || 'Order';
        const titleEl = document.createElement('h3');
        titleEl.className = 'h4 mb-4';
        titleEl.textContent = `${orderLabel}: ${orderName}`;
        wrapper.appendChild(titleEl);
      }

      const msg = document.createElement('p');
      msg.className = 'opacity-70';
      msg.textContent = this.t.noEligibleItems || 'No items are eligible for a warranty claim.';
      wrapper.appendChild(msg);

      const actions = document.createElement('div');
      actions.className = 'return-form__actions';
      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'btn btn--secondary';
      backBtn.dataset.cancelReturn = '';
      backBtn.textContent = this.t.cancel || 'Back';
      actions.appendChild(backBtn);
      wrapper.appendChild(actions);

      container.appendChild(wrapper);
    }

    makeEmptyClaimGroup() {
      return { quantity: 0, reason: '', solution: '', solutionLabel: '' };
    }

    formatFulfillmentHeading(group) {
      const invoiceLabel = this.t.invoiceLabel || 'Invoice';
      const displayId = group.docNumber || group.documentId;
      return displayId ? `${invoiceLabel}: ${displayId}` : '';
    }

    findItemEligibleQuantity(itemKey) {
      const item = this.claimableItems.find((i) => i.itemKey === itemKey);
      return item ? Number(item.eligibleQuantity) || 0 : 0;
    }

    refreshQtyOptionsForItem(itemKey) {
      const itemEl = this.claimContainer?.querySelector(
        `[data-return-item][data-line-item-id="${CSS.escape(itemKey)}"]`
      );
      if (!itemEl) return;
      const eligible = this.findItemEligibleQuantity(itemKey);
      const groups = this.getClaimGroups(itemKey);
      const totalUsed = groups.reduce((sum, g) => sum + (Number(g.quantity) || 0), 0);
      // Only surface "+ Add another reason" once the user has committed a quantity
      // to the first group; disable when capacity is exhausted.
      const addBtnWrapper = itemEl.querySelector('.return-item__add-group');
      const addBtn = itemEl.querySelector('[data-add-group]');
      if (addBtnWrapper) addBtnWrapper.classList.toggle('hidden', totalUsed === 0);
      if (addBtn) addBtn.disabled = totalUsed >= eligible;
      const groupEls = itemEl.querySelectorAll('[data-claim-group]');
      groupEls.forEach((groupEl) => {
        const idx = Number(groupEl.dataset.groupIndex);
        if (!Number.isInteger(idx)) return;
        const claim = groups[idx];
        if (!claim) return;
        const ownQty = Number(claim.quantity) || 0;
        const max = Math.max(0, eligible - (totalUsed - ownQty));
        const select = groupEl.querySelector('[data-return-qty-select]');
        if (!select) return;
        const currentValue = select.value;
        while (select.firstChild) select.removeChild(select.firstChild);
        const emptyOpt = document.createElement('option');
        emptyOpt.value = '';
        emptyOpt.textContent = '';
        select.appendChild(emptyOpt);
        for (let q = 1; q <= max; q++) {
          const opt = document.createElement('option');
          opt.value = String(q);
          opt.textContent = String(q);
          select.appendChild(opt);
        }
        // Preserve user's selection if it's still valid; otherwise fall back to empty.
        const desired = ownQty >= 1 ? String(ownQty) : currentValue;
        select.value = Array.from(select.options).some((o) => o.value === desired) ? desired : '';
      });
    }

    buildClaimGroupElement(item, claim, groupIndex, totalGroups, itemIndex) {
      const groupFragment = this.templates.claimGroup.content.cloneNode(true);
      const groupEl = groupFragment.querySelector('[data-claim-group]');
      groupEl.dataset.groupIndex = String(groupIndex);

      const qtySelect = groupEl.querySelector('[data-return-qty-select]');
      const qtyLabel = groupEl.querySelector('[data-qty-label]');
      qtySelect.id = `return-qty-${itemIndex}`;
      if (qtyLabel) qtyLabel.setAttribute('for', qtySelect.id);
      const emptyOpt = document.createElement('option');
      emptyOpt.value = '';
      emptyOpt.textContent = '';
      qtySelect.appendChild(emptyOpt);
      for (let q = 1; q <= item.eligibleQuantity; q++) {
        const opt = document.createElement('option');
        opt.value = q;
        opt.textContent = q;
        qtySelect.appendChild(opt);
      }
      qtySelect.value = claim.quantity >= 1 ? String(claim.quantity) : '';

      const removeBtn = groupEl.querySelector('[data-remove-group]');
      if (removeBtn) removeBtn.classList.toggle('hidden', totalGroups <= 1);

      const isSelected = claim.quantity >= 1;

      const reasonWrapper = groupEl.querySelector('[data-item-reason-wrapper]');
      const reasonInput = groupEl.querySelector('[data-item-reason]');
      if (reasonInput) {
        reasonInput.id = `return-reason-${itemIndex}`;
        reasonInput.value = claim.reason || '';
      }
      if (reasonWrapper) {
        const reasonLabel = reasonWrapper.querySelector('[data-item-reason-label]');
        if (reasonLabel && reasonInput) reasonLabel.setAttribute('for', reasonInput.id);
        reasonWrapper.classList.toggle('hidden', !isSelected);
      }

      const solutionWrapper = groupEl.querySelector('[data-item-solution-wrapper]');
      const solutionInput = groupEl.querySelector('[data-item-solution]');
      if (solutionInput) {
        solutionInput.id = `return-solution-${itemIndex}`;
        solutionInput.value = claim.solution || '';
      }
      if (solutionWrapper) {
        const solutionLabel = solutionWrapper.querySelector('[data-item-solution-label]');
        if (solutionLabel && solutionInput) solutionLabel.setAttribute('for', solutionInput.id);
        solutionWrapper.classList.toggle('hidden', !isSelected);
      }

      return groupFragment;
    }

    handleGroupQty(target, quantity) {
      const q = parseInt(quantity, 10);
      const newQty = Number.isInteger(q) && q >= 1 ? q : 0;
      const groups = this.getClaimGroups(target.itemKey).slice();
      const claim = groups[target.groupIndex];
      if (!claim) return;
      groups[target.groupIndex] = { ...claim, quantity: newQty };
      // Reset reason/solution when deselected so we don't ship stale data on resubmit.
      if (newQty === 0) {
        groups[target.groupIndex] = { ...groups[target.groupIndex], reason: '', solution: '', solutionLabel: '' };
      }
      this.setClaimGroups(target.itemKey, groups);

      const reasonWrapper = target.groupEl?.querySelector('[data-item-reason-wrapper]');
      if (reasonWrapper) reasonWrapper.classList.toggle('hidden', newQty < 1);
      const solutionWrapper = target.groupEl?.querySelector('[data-item-solution-wrapper]');
      if (solutionWrapper) solutionWrapper.classList.toggle('hidden', newQty < 1);
      if (newQty === 0) {
        const reasonInput = target.groupEl?.querySelector('[data-item-reason]');
        if (reasonInput) reasonInput.value = '';
        const solutionSelect = target.groupEl?.querySelector('[data-item-solution]');
        if (solutionSelect) solutionSelect.value = '';
      }

      this.refreshQtyOptionsForItem(target.itemKey);
      this.hideInlineClaimError();
      this.updateContinueButton();
    }

    handleGroupReason(target, value) {
      const groups = this.getClaimGroups(target.itemKey).slice();
      const claim = groups[target.groupIndex];
      if (!claim) return;
      groups[target.groupIndex] = { ...claim, reason: value };
      this.setClaimGroups(target.itemKey, groups);
      this.hideInlineClaimError();
      this.updateContinueButton();
    }

    handleGroupSolution(target, value, label = '') {
      const groups = this.getClaimGroups(target.itemKey).slice();
      const claim = groups[target.groupIndex];
      if (!claim) return;
      groups[target.groupIndex] = { ...claim, solution: value, solutionLabel: label };
      this.setClaimGroups(target.itemKey, groups);
      this.hideInlineClaimError();
      this.updateContinueButton();
    }

    handleAddGroup(itemKey) {
      const groups = this.getClaimGroups(itemKey).slice();
      const newGroup = this.makeEmptyClaimGroup();
      // Default new group to 1 if there's remaining capacity, otherwise leave at 0
      // and let the user free up quantity in another group first.
      const eligible = this.findItemEligibleQuantity(itemKey);
      const used = groups.reduce((sum, g) => sum + (Number(g.quantity) || 0), 0);
      if (used < eligible) newGroup.quantity = 1;
      groups.push(newGroup);
      this.setClaimGroups(itemKey, groups);
      this.renderClaimForm();
      this.hideInlineClaimError();
    }

    handleRemoveGroup(target) {
      const groups = this.getClaimGroups(target.itemKey).slice();
      if (groups.length <= 1) return;
      groups.splice(target.groupIndex, 1);
      this.setClaimGroups(target.itemKey, groups);
      this.renderClaimForm();
      this.hideInlineClaimError();
    }

    handleAddressSelect(value) {
      if (!value) {
        this.selectedAddress = null;
      } else {
        const { isB2B, options } = this.getCustomerAddressOptions();
        const selected = options.find(o => o.id === value);
        if (selected) {
          this.selectedAddress = {
            id: value,
            type: isB2B ? 'location' : 'address',
            display: this.formatAddressDisplay(selected, isB2B) || value
          };
        } else {
          this.selectedAddress = null;
        }
      }
      this.hideInlineClaimError();
      this.updateContinueButton();
    }

    updateContinueButton() {
      const continueBtn = this.claimContainer?.querySelector('[data-continue-return]');
      if (!continueBtn) return;
      const result = this.validateClaimGroups();
      const isDisabled = !result.hasSelectedGroup || !result.complete || result.exceedsQty;
      continueBtn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      continueBtn.classList.toggle('is-disabled', isDisabled);
    }

    validateClaimGroups() {
      let hasSelectedGroup = false;
      let missingReason = false;
      let missingSolution = false;
      let exceedsQty = false;
      const eligibleByKey = new Map(this.claimableItems.map((i) => [i.itemKey, i.eligibleQuantity]));

      for (const [itemKey, groups] of this.itemClaims.entries()) {
        let totalQty = 0;
        for (const claim of groups) {
          const q = Number(claim.quantity) || 0;
          if (q < 1) continue;
          hasSelectedGroup = true;
          totalQty += q;
          if (!claim.reason || claim.reason.trim() === '') missingReason = true;
          if (!claim.solution || claim.solution.trim() === '') missingSolution = true;
        }
        const eligible = eligibleByKey.get(itemKey);
        if (typeof eligible === 'number' && totalQty > eligible) exceedsQty = true;
      }

      return {
        hasSelectedGroup,
        missingReason,
        missingSolution,
        exceedsQty,
        complete: !missingReason && !missingSolution
      };
    }

    showInlineClaimError(message) {
      const errEl = this.claimContainer?.querySelector('[data-claim-inline-error]');
      if (!errEl) return;
      errEl.textContent = message;
      errEl.classList.remove('hidden');
    }

    hideInlineClaimError() {
      const errEl = this.claimContainer?.querySelector('[data-claim-inline-error]');
      if (!errEl) return;
      errEl.classList.add('hidden');
      errEl.textContent = '';
    }

    showReviewStep() {
      const result = this.validateClaimGroups();
      if (!result.hasSelectedGroup) {
        this.showInlineClaimError(this.t.claimErrorNoItems || 'Select at least one item');
        return;
      }
      if (result.exceedsQty) {
        this.showInlineClaimError(this.t.claimErrorQuantityExceeded || 'Total quantity exceeds the available amount.');
        return;
      }
      if (result.missingReason) {
        this.showInlineClaimError(this.t.claimErrorNoReason || 'Please describe the issue');
        return;
      }
      if (result.missingSolution) {
        this.showInlineClaimError(this.t.claimErrorNoSolution || 'Please describe your desired resolution');
        return;
      }
      this.renderReviewStep();
    }

    renderReviewStep() {
      const container = this.claimContainer;
      if (!this.templates.returnReview || !this.templates.reviewItem || !container) return;

      container.replaceChildren();
      const reviewEl = this.templates.returnReview.content.cloneNode(true);

      // Append order name to the title already set by the Liquid template
      const titleEl = reviewEl.querySelector('[data-review-title]');
      const orderName = this.currentOrder?.name || '';
      if (orderName && titleEl) {
        const orderLabel = this.dataset.labelOrder || 'Order';
        titleEl.textContent = `${titleEl.textContent} \u2014 ${orderLabel}: ${orderName}`;
      }

      // Render address selector on the review step
      const reviewAddressSelectorEl = reviewEl.querySelector('[data-review-address-selector]');
      if (reviewAddressSelectorEl) {
        this.renderAddressSelector(reviewAddressSelectorEl);
      }

      const itemsContainer = reviewEl.querySelector('[data-review-items]');
      this.fulfillmentGroups.forEach(group => {
        const itemsWithSelected = group.items
          .map((item) => ({ item, claims: this.getClaimGroups(item.itemKey).filter((g) => Number(g.quantity) >= 1) }))
          .filter(({ claims }) => claims.length > 0);
        if (itemsWithSelected.length === 0) return;

        const headingText = this.formatFulfillmentHeading(group);
        if (headingText) {
          const heading = document.createElement('div');
          heading.className = 'return-review__fulfillment-heading';
          heading.textContent = headingText;
          itemsContainer.appendChild(heading);
        }

        itemsWithSelected.forEach(({ item, claims }) => {
          claims.forEach((claim) => {
            const row = this.templates.reviewItem.content.cloneNode(true);
            row.querySelector('[data-title]').textContent = item.title;
            const skuEl = row.querySelector('[data-sku]');
            if (skuEl) {
              if (item.sku) skuEl.textContent = `SKU: ${item.sku}`;
              else skuEl.remove();
            }

            this.appendWarrantyBadge(row.querySelector('.return-review__item-info'), item);
            const qtyEl = row.querySelector('[data-qty]');
            if (qtyEl) {
              const qtyLabel = qtyEl.dataset.qtyLabel || 'Qty';
              qtyEl.textContent = `${qtyLabel}: ${claim.quantity}`;
            }
            this.renderItemImage(row.querySelector('[data-image]'), item.imageUrl);
            const reasonEl = row.querySelector('[data-reason]');
            if (reasonEl) reasonEl.textContent = claim.reason || '';
            const solutionEl = row.querySelector('[data-solution]');
            if (solutionEl) solutionEl.textContent = claim.solutionLabel || '';
            itemsContainer.appendChild(row);
          });
        });
      });

      container.appendChild(reviewEl);
      this.initTextareasInEl(container);
    }

    async submitReturnRequest() {
      const endpoint = this.getRequestReturnEndpoint();
      if (!endpoint) {
        this.showError(this.t.errorConfig);
        return;
      }
      if (!this.currentOrder) {
        this.showError(this.t.returnErrorNoOrder || 'Order not found');
        return;
      }
      const email = (this.lookupEmail || '').trim();
      if (!email) {
        this.showError(this.t.errorInvalidEmail || 'Email is required');
        return;
      }
      const addressRequired = this.getCustomerAddressOptions().options.length > 0;
      if (addressRequired && !this.selectedAddress) {
        this.showError(this.t.claimErrorNoAddress || 'Please select an address');
        return;
      }

      const validation = this.validateClaimGroups();
      if (!validation.hasSelectedGroup) {
        this.showError(this.t.claimErrorNoItems || 'Select at least one item');
        return;
      }
      if (validation.exceedsQty) {
        this.showError(this.t.claimErrorQuantityExceeded || 'Total quantity exceeds the available amount.');
        return;
      }
      if (validation.missingReason) {
        this.showError(this.t.claimErrorNoReason || 'Please describe the issue');
        return;
      }
      if (validation.missingSolution) {
        this.showError(this.t.claimErrorNoSolution || 'Please describe your desired resolution');
        return;
      }

      const items = [];
      for (const [itemKey, groups] of this.itemClaims.entries()) {
        const claimItem = this.claimableItems.find((i) => i.itemKey === itemKey);
        if (!claimItem) continue;
        for (const claim of groups) {
          const quantity = Number(claim.quantity) || 0;
          if (quantity < 1) continue;
          items.push({
            documentId: claimItem.documentId || '',
            orderNumber: claimItem.orderNumber || this.orderNumber || '',
            itemSku: claimItem.sku || '',
            itemQuantity: quantity,
            reason: (claim.reason || '').trim(),
            solution: (claim.solution || '').trim()
          });
        }
      }

      const container = this.claimContainer;
      const confirmBtn = container?.querySelector('[data-submit-return]');
      const backBtn = container?.querySelector('[data-back-to-edit]');
      if (confirmBtn) {
        confirmBtn.disabled = true;
        // Loading text is pre-rendered in Liquid via data-loading-text attribute
        confirmBtn.textContent = confirmBtn.dataset.loadingText || 'Submitting...';
      }
      if (backBtn) backBtn.disabled = true;

      const resetButtons = () => {
        if (confirmBtn) {
          confirmBtn.disabled = false;
          // Restore original confirm text from Liquid data attribute
          confirmBtn.textContent = confirmBtn.dataset.confirmText || 'Confirm and send';
        }
        if (backBtn) backBtn.disabled = false;
      };

      const payload = { email, items };
      if (this.selectedAddress?.type === 'location') {
        payload.companyLocationId = this.selectedAddress.id;
        payload.customerAddressId = null;
      } else if (this.selectedAddress) {
        payload.companyLocationId = null;
        payload.customerAddressId = this.selectedAddress.id;
      }

      try {
        // Per FE notes (5): do not auto-retry on 500; the user decides.
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();

        if (data.success) {
          this.resetClaimState();
          this.showClaimSuccess();
          return;
        }

        this.showError(this.t.claimErrorSubmit || 'Failed to submit claim');
        resetButtons();
      } catch {
        this.showError(this.t.claimErrorSubmit || 'Failed to submit claim');
        resetButtons();
      }
    }

    async showClaimSuccess() {
      const msg = this.t.claimSuccessMessage || 'Your warranty claim has been submitted.';
      const container = this.claimContainer;

      const buildSuccessEl = () => {
        const el = document.createElement('div');
        el.className = 'alert mb-4';
        el.setAttribute('role', 'status');
        el.style.cssText = 'background: rgb(var(--ol-success-bg)); color: rgb(var(--ol-success-text)); padding: 1rem;';
        el.textContent = msg;
        return el;
      };

      // Immediately clear the review step (with its stuck "Submitting..." button)
      // and show the success message. This must happen BEFORE any awaits so the
      // user never sees the disabled button lingering if the re-fetch stalls or fails.
      if (container) {
        container.replaceChildren(buildSuccessEl());
      }

      setTimeout(() => {
        window.location.href = window.location.pathname;
      }, 5000);
    }

    renderItemImage(container, imageUrl) {
      if (!container || !imageUrl) return;
      while (container.firstChild) container.removeChild(container.firstChild);
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = '';
      container.appendChild(img);
    }

    appendWarrantyBadge(parentEl, item) {
      if (!parentEl || !item?.warrantyUntil) return;
      const inWarranty = new Date().toISOString().slice(0, 10) <= item.warrantyUntil;
      const badge = document.createElement('span');
      badge.className = inWarranty
        ? 'return-item__warranty-badge return-item__warranty-badge--in'
        : 'return-item__warranty-badge return-item__warranty-badge--out';
      badge.textContent = inWarranty
        ? (this.t.inWarranty || 'In warranty')
        : (this.t.outOfWarranty || 'Out of warranty');
      parentEl.appendChild(badge);
    }

    // === UI State ===

    showView(name) {
      Object.entries(this.views).forEach(([key, el]) => {
        el?.classList.toggle('is-active', key === name);
      });
      if (name === 'form') {
        this.form?.orderName?.focus();
      }
    }

    setLoading(loading) {
      if (this.submitBtn) {
        this.submitBtn.disabled = loading;
        this.submitBtn.querySelector('[data-btn-text]')?.classList.toggle('hidden', loading);
        this.submitBtn.querySelector('[data-btn-loader]')?.classList.toggle('hidden', !loading);
      }
      if (this.form) {
        this.form.orderName.disabled = loading;
        this.form.email.disabled = loading;
      }
    }

    showError(message) {
      const formViewActive = this.views.form?.classList.contains('is-active');
      if (formViewActive) {
        if (this.errorMessage) this.errorMessage.textContent = message;
        this.errorContainer?.classList.remove('hidden');
      } else {
        this.showInlineClaimError(message);
      }
    }

    hideError() {
      this.errorContainer?.classList.add('hidden');
    }
  }

  customElements.define('order-lookup', OrderLookup);
}
